import { Model, Factory, belongsTo, hasMany } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import {
  rand,
  randRecentDate,
  randNumber,
  randChanceBoolean,
} from '@ngneat/falso'

import type { MockServer, MirageServer } from './server.js'
import { pageable, getExistingEntity } from './utils.js'
import { addToDate } from '@/utils/date/calculation.js'
import { formatToDateTimeISO } from '@/utils/date/conversion.js'
import type { GeneralPrescriptionAttributes } from '@/composables/generalPrescriptions.js'

export const model = (
  Model as ModelDefinition<GeneralPrescriptionAttributes>
).extend({
  consultation: belongsTo(),
  prescriber: belongsTo('user'),
  items: hasMany('generalPrescriptionItem'),
})

export const factory = Factory.extend<GeneralPrescriptionAttributes>({
  prescription_type() {
    return rand(['phone', 'written'])
  },
  issue_time() {
    return randRecentDate({ days: 30 }).toISOString()
  },
  valid_months() {
    return randChanceBoolean({ chanceTrue: 0.7 })
      ? randNumber({ min: 1, max: 12 })
      : null
  },
  valid_days() {
    return randChanceBoolean({ chanceTrue: 0.3 })
      ? randNumber({ min: 7, max: 90 })
      : null
  },
  diagnosis() {
    return randChanceBoolean({ chanceTrue: 0.8 })
      ? rand([
          'Bacterial infection',
          'Inflammation',
          'Pain management',
          'Preventive care',
          'Chronic condition',
          'Acute condition',
        ])
      : null
  },
  diagnosis_type() {
    return randChanceBoolean({ chanceTrue: 0.7 })
      ? randNumber({ min: 1, max: 10 })
      : null
  },
  diagnosis_type_display() {
    return randChanceBoolean({ chanceTrue: 0.7 })
      ? rand([
          'Clinical diagnosis',
          'Laboratory diagnosis',
          'Imaging diagnosis',
          'Physical examination',
        ])
      : null
  },
  diagnosis_reason_type() {
    return randChanceBoolean({ chanceTrue: 0.6 })
      ? randNumber({ min: 1, max: 10 })
      : null
  },
  printed_time() {
    return randChanceBoolean({ chanceTrue: 0.4 })
      ? randRecentDate({ days: 7 }).toISOString()
      : null
  },
  archived() {
    return false
  },
  created() {
    return randRecentDate({ days: 60 }).toISOString()
  },
  modified() {
    const created = this.created as string
    return randChanceBoolean({ chanceTrue: 0.6 })
      ? randRecentDate({ days: 30 }).toISOString()
      : created
  },
  fee() {
    return randChanceBoolean({ chanceTrue: 0.5 })
      ? randNumber({ min: 10, max: 50, precision: 2 })
      : null
  },
  sent_pharmacy() {
    return randChanceBoolean({ chanceTrue: 0.3 })
      ? randRecentDate({ days: 7 }).toISOString()
      : null
  },
  sent_client() {
    return randChanceBoolean({ chanceTrue: 0.4 })
      ? randRecentDate({ days: 7 }).toISOString()
      : null
  },
  expiration_date() {
    const validMonths = this.valid_months
    const validDays = this.valid_days
    const issueTime = this.issue_time

    if (!validMonths && !validDays) {
      return null
    }

    if (!issueTime || typeof issueTime !== 'string') {
      return null
    }

    const amount = validMonths
      ? { months: Number(validMonths) }
      : { days: Number(validDays) }
    const expirationDate = addToDate(issueTime, amount)

    return formatToDateTimeISO(expirationDate) ?? ''
  },

  afterCreate(prescription, server) {
    if (!prescription.prescriber) {
      const prescriber = getExistingEntity(server, 'user')
      prescription.update({ prescriber })
    }

    const itemCount = randNumber({ min: 2, max: 3 })
    const items = []
    for (let i = 0; i < itemCount; i++) {
      const item = server.create('generalPrescriptionItem')
      items.push(item)
    }
    prescription.update({ items })
  },
})

export function routes(server: MockServer) {
  server.get(
    '/general_prescriptions',
    function (this: MirageServer, schema, request) {
      const consultationId = request.queryParams['filter[consultation_id]']
      const archived = request.queryParams['filter[archived]']

      let prescriptions = schema.all('generalPrescription')

      if (consultationId) {
        prescriptions = prescriptions.filter(
          (prescription) => prescription.consultation?.id === consultationId,
        )
      }

      const archivedBool = archived === 'true'
      prescriptions = prescriptions.filter(
        (prescription) => prescription.archived === archivedBool,
      )

      const { data, meta } = pageable(prescriptions, request)

      return {
        ...this.serialize(data),
        meta: {
          pagination: meta,
        },
      }
    },
  )
}

export function seeds(server: MockServer) {
  const consultationIds = ['1', '2']

  consultationIds.forEach((id) => {
    const consultation = server.schema.find('consultation', id)

    if (consultation) {
      const prescriptionCount = randNumber({ min: 1, max: 3 })
      for (let i = 0; i < prescriptionCount; i++) {
        server.create('generalPrescription', {
          consultation,
        })
      }
    }
  })
}
